import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  BottomDrawer,
  Button,
  EmptyState,
  FormField,
  Group,
  PageHeader,
  Paper,
  Stack,
  TabView,
  Text,
  TextInput,
  Title,
} from '@vipso/shared';
import {
  useCompleteTaskMutation,
  useMyTasksQuery,
  useResolveTaskMutation,
} from '../api/tenantApi';
import { setPendingTaskCount } from '../store/authSlice';

type TaskRow = NonNullable<ReturnType<typeof useMyTasksQuery>['data']>['myTasks'][number];

export function TasksPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [category, setCategory] = useState('all');
  const [activeTask, setActiveTask] = useState<TaskRow | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [addressForm, setAddressForm] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'IN',
  });
  const [contactForm, setContactForm] = useState({
    businessEmail: '',
    contactPersonPhone: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });

  const { data, isLoading, isError, refetch } = useMyTasksQuery(
    category === 'all' ? undefined : { category },
  );
  const [completeTask, completeState] = useCompleteTaskMutation();
  const [resolveTask, resolveState] = useResolveTaskMutation();

  const tasks = data?.myTasks ?? [];
  const pending = useMemo(() => tasks.filter((task) => task.status === 'PENDING'), [tasks]);
  const criticalPending = useMemo(
    () =>
      pending.filter(
        (task) => task.priority === 'CRITICAL' || task.template?.priority === 'CRITICAL',
      ),
    [pending],
  );

  const [prompted, setPrompted] = useState(false);

  useEffect(() => {
    dispatch(setPendingTaskCount(pending.length));
  }, [pending.length, dispatch]);

  useEffect(() => {
    if (!prompted && !isLoading && criticalPending.length > 0) {
      setActiveTask(criticalPending[0]);
      setPrompted(true);
    }
  }, [prompted, isLoading, criticalPending]);

  async function handleComplete(input: Record<string, unknown>) {
    if (!activeTask) return;
    setFormError(null);
    try {
      if (activeTask.template?.kind === 'APPROVAL') {
        await resolveTask({
          instanceId: activeTask.id,
          decision: input.decision as 'APPROVED' | 'REJECTED',
          note: input.note as string | undefined,
        }).unwrap();
      } else {
        await completeTask({ instanceId: activeTask.id, input }).unwrap();
      }
      setActiveTask(null);
      await refetch();
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message ?? '')
          : '';
      const fallback =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message ?? '')
          : '';
      setFormError(message || fallback || t('tasks.completeFailed'));
    }
  }

  function handleTaskClick(task: TaskRow) {
    const SUBJECT_ROUTES: Record<string, string> = {
      PROC: '/orders',
      CUSTOMER: '/customers',
      VENDOR: '/vendors',
      PRODUCT: '/products',
      WAREHOUSE: '/warehouse',
    };
    const basePath = SUBJECT_ROUTES[task.subjectType ?? ''];
    if (basePath && task.subjectId) {
      navigate(`${basePath}/${task.subjectId}`);
      return;
    }
    if (task.status === 'PENDING') openTask(task);
  }

  function openTask(task: TaskRow) {
    setFormError(null);
    setActiveTask(task);
    setAddressForm({
      line1: '',
      line2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'IN',
    });
    setContactForm({ businessEmail: '', contactPersonPhone: '' });
    setPasswordForm({ password: '', confirmPassword: '' });
  }

  const taskList = (
    <Stack gap="sm">
      {isLoading ? (
        <Text c="dimmed">{t('tasks.loading')}</Text>
      ) : isError ? (
        <Alert color="red" title={t('tasks.loadFailed')}>
          {t('common.connectionError')}
        </Alert>
      ) : tasks.length === 0 ? (
        <EmptyState title={t('tasks.emptyTitle')} description={t('tasks.emptyDescription')} />
      ) : (
        tasks.map((task) => (
          <Paper
            key={task.id}
            p="md"
            withBorder
            radius="md"
            style={{
              cursor:
                task.status === 'PENDING' || (task.subjectType && task.subjectId)
                  ? 'pointer'
                  : undefined,
            }}
            onClick={() => handleTaskClick(task)}
          >
            <Group justify="space-between" align="flex-start">
              <Stack gap={4}>
                <Text fw={600}>{task.template?.title ?? t('tasks.defaultTitle')}</Text>
                <Text size="sm" c="dimmed">
                  {task.template?.description || task.template?.code}
                </Text>
                <Group gap="xs">
                  <Badge size="sm" variant="light">
                    {task.template?.category ?? 'GENERAL'}
                  </Badge>
                  <Badge
                    size="sm"
                    color={
                      task.priority === 'CRITICAL' || task.template?.priority === 'CRITICAL'
                        ? 'red'
                        : 'gray'
                    }
                  >
                    {task.priority || task.template?.priority}
                  </Badge>
                </Group>
              </Stack>
              <Badge color={task.status === 'PENDING' ? 'orange' : 'green'}>{task.status}</Badge>
            </Group>
          </Paper>
        ))
      )}
    </Stack>
  );

  const actionKey = activeTask?.template?.actionKey;
  const saving = completeState.isLoading || resolveState.isLoading;

  return (
    <Stack gap="lg">
      <PageHeader title={t('nav.tasks')} description={t('tasks.description')} />

      {pending.length > 0 ? (
        <Alert color="orange" title={t('tasks.pendingAlert', { count: pending.length })}>
          {t('tasks.pendingHint')}
        </Alert>
      ) : null}

      <TabView
        value={category}
        onChange={setCategory}
        tabs={[
          { value: 'all', title: t('common.all'), content: taskList },
          { value: 'ONBOARDING', title: t('tasks.onboarding'), content: taskList },
          { value: 'APPROVAL', title: t('tasks.approvals'), content: taskList },
        ]}
      />

      <BottomDrawer
        opened={Boolean(activeTask)}
        onClose={() => setActiveTask(null)}
        title={activeTask?.template?.title ?? t('tasks.completeTask')}
      >
        <Stack gap="md">
          {activeTask?.template?.description ? (
            <Text size="sm" c="dimmed">
              {activeTask.template.description}
            </Text>
          ) : null}

          {formError ? <Alert color="red" title={formError} /> : null}

          {activeTask?.template?.kind === 'APPROVAL' ? (
            <Stack gap="sm">
              <Text size="sm">
                {activeTask.template.actionKey === 'approval.rejection_ack'
                  ? t('approval.acknowledgeRejection')
                  : t('tasks.approvalPlaceholder')}
              </Text>
              <Group justify="flex-end">
                {activeTask.template.actionKey !== 'approval.rejection_ack' ? (
                  <Button
                    variant="default"
                    loading={saving}
                    onClick={() => void handleComplete({ decision: 'REJECTED' })}
                  >
                    {t('tasks.reject')}
                  </Button>
                ) : null}
                <Button loading={saving} onClick={() => void handleComplete({ decision: 'APPROVED' })}>
                  {activeTask.template.actionKey === 'approval.rejection_ack'
                    ? t('approval.acknowledge')
                    : t('tasks.approve')}
                </Button>
              </Group>
            </Stack>
          ) : null}

          {actionKey === 'address' || actionKey === 'staff_address' ? (
            <Stack gap="md">
              <FormField label={t('address.line1')} required>
                <TextInput
                  value={addressForm.line1}
                  onChange={(e) => {
                    const line1 = e.currentTarget.value;
                    setAddressForm((f) => ({ ...f, line1 }));
                  }}
                />
              </FormField>
              <FormField label={t('address.line2')}>
                <TextInput
                  value={addressForm.line2}
                  onChange={(e) => {
                    const line2 = e.currentTarget.value;
                    setAddressForm((f) => ({ ...f, line2 }));
                  }}
                />
              </FormField>
              <FormField label={t('address.city')} required>
                <TextInput
                  value={addressForm.city}
                  onChange={(e) => {
                    const city = e.currentTarget.value;
                    setAddressForm((f) => ({ ...f, city }));
                  }}
                />
              </FormField>
              <FormField label={t('address.state')}>
                <TextInput
                  value={addressForm.state}
                  onChange={(e) => {
                    const state = e.currentTarget.value;
                    setAddressForm((f) => ({ ...f, state }));
                  }}
                />
              </FormField>
              <FormField label={t('address.postalCode')}>
                <TextInput
                  value={addressForm.postalCode}
                  onChange={(e) => {
                    const postalCode = e.currentTarget.value;
                    setAddressForm((f) => ({ ...f, postalCode }));
                  }}
                />
              </FormField>
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setActiveTask(null)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  loading={saving}
                  disabled={!addressForm.line1.trim() || !addressForm.city.trim()}
                  onClick={() => void handleComplete(addressForm)}
                >
                  {t('common.save')}
                </Button>
              </Group>
            </Stack>
          ) : null}

          {actionKey === 'org_business_contact' ? (
            <Stack gap="md">
              <FormField label={t('tasks.businessEmail')} required>
                <TextInput
                  type="email"
                  value={contactForm.businessEmail}
                  onChange={(e) => {
                    const businessEmail = e.currentTarget.value;
                    setContactForm((f) => ({ ...f, businessEmail }));
                  }}
                />
              </FormField>
              <FormField label={t('tasks.contactPhone')} required>
                <TextInput
                  value={contactForm.contactPersonPhone}
                  onChange={(e) => {
                    const contactPersonPhone = e.currentTarget.value;
                    setContactForm((f) => ({ ...f, contactPersonPhone }));
                  }}
                />
              </FormField>
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setActiveTask(null)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  loading={saving}
                  disabled={
                    !contactForm.businessEmail.trim() || !contactForm.contactPersonPhone.trim()
                  }
                  onClick={() => void handleComplete(contactForm)}
                >
                  {t('common.save')}
                </Button>
              </Group>
            </Stack>
          ) : null}

          {actionKey === 'change_password' ? (
            <Stack gap="md">
              <FormField label={t('auth.newPassword')} required>
                <TextInput
                  type="password"
                  value={passwordForm.password}
                  onChange={(e) => {
                    const password = e.currentTarget.value;
                    setPasswordForm((f) => ({ ...f, password }));
                  }}
                />
              </FormField>
              <FormField label={t('auth.confirmPassword')} required>
                <TextInput
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => {
                    const confirmPassword = e.currentTarget.value;
                    setPasswordForm((f) => ({ ...f, confirmPassword }));
                  }}
                />
              </FormField>
              <Group justify="flex-end">
                <Button variant="default" onClick={() => setActiveTask(null)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  loading={saving}
                  disabled={
                    passwordForm.password.length < 6 ||
                    passwordForm.password !== passwordForm.confirmPassword
                  }
                  onClick={() => void handleComplete(passwordForm)}
                >
                  {t('auth.savePassword')}
                </Button>
              </Group>
            </Stack>
          ) : null}

          {activeTask &&
          activeTask.template?.kind === 'FORM' &&
          !['address', 'staff_address', 'org_business_contact', 'change_password'].includes(
            actionKey ?? '',
          ) ? (
            <Stack gap="sm">
              <Title order={5}>{t('tasks.unsupportedFormTitle')}</Title>
              <Text size="sm" c="dimmed">
                {t('tasks.unsupportedForm', { key: actionKey })}
              </Text>
            </Stack>
          ) : null}
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
